import {
  BEERPONG_BALL_RADIUS,
  BEERPONG_CUP_HEIGHT,
  BEERPONG_CUP_RADIUS,
  BEERPONG_TABLE_LENGTH,
  BEERPONG_TABLE_WIDTH,
  mirrorPoint,
  type BeerPongBallPoint,
  type BeerPongPoint,
} from '@awwyeah/shared';

/**
 * Canvas rendering for the Beer Pong table. Kept free of React (same split as Pool's
 * `pool/render.ts` and SketchPad's `sketch/canvas.ts`) so the geometry — `beerPongLayout`,
 * `toCanvas`, `toTable` — is unit-testable without a DOM, and the component only ever calls
 * `drawTable`.
 *
 * **Theme policy**, inherited verbatim from Pool: the table and the cups are the *object*,
 * not chrome. A table is a table and a cup is red in every theme, the same way a photo
 * doesn't recolour in dark mode. Everything *around* it — header, racks, buttons, status
 * text — is semantic tokens in `BeerPongBoardPage.tsx` and themes normally. This file holds a
 * handful of fixed object colours and reads exactly one thing from the theme: the current
 * thrower's member colour, which the caller passes in.
 *
 * **The one perspective trick.** This is a top-down view of a game whose whole story is
 * height, so the ball's `z` is drawn two ways at once: the ball grows a little as it rises,
 * and a shadow stays on the table beneath it, shrinking and darkening as the ball comes down
 * to meet it. That's the entire 3D budget — no isometric projection, no tilt, nothing that
 * would make the cups harder to aim at, which is what the player is actually here to do.
 */

const TABLE = '#c98f55';
const TABLE_DARK = '#a86e3a';
const TABLE_EDGE = '#6e4422';
const TABLE_LINE = 'rgba(255,255,255,0.5)';
const CUP_RIM = '#e2473f';
const CUP_WALL = '#b3271f';
const CUP_WALL_DARK = '#7d1712';
const BEER = '#e0a43a';
const BEER_DEEP = '#a8681a';
const FOAM = 'rgba(255,248,228,0.9)';
const CUP_GHOST = 'rgba(70,35,10,0.28)';
const BALL = '#fbf8f0';
const BALL_EDGE = 'rgba(0,0,0,0.35)';

/**
 * Where the table sits inside the canvas, and how table units map to CSS pixels.
 *
 * Mobile-first orientation: in a portrait box the table stands up (long axis vertical) with
 * the viewer's own end at the *bottom*, so you throw up the screen and the cups you're aiming
 * at are up the far end. In a landscape box it lies flat. Nothing else in the module knows
 * which of the two happened — everything goes through `toCanvas`/`toTable`.
 */
export interface BeerPongLayout {
  /** CSS pixels per table unit. */
  scale: number;
  originX: number;
  originY: number;
  vertical: boolean;
  cssWidth: number;
  cssHeight: number;
}

/** The painted border drawn outside the playing surface, in table units. */
export const EDGE_WIDTH = 0.06;

export function beerPongLayout(cssWidth: number, cssHeight: number): BeerPongLayout {
  const outerLong = BEERPONG_TABLE_LENGTH + EDGE_WIDTH * 2;
  const outerShort = BEERPONG_TABLE_WIDTH + EDGE_WIDTH * 2;
  const vertical = cssHeight > cssWidth;
  const boxLong = vertical ? cssHeight : cssWidth;
  const boxShort = vertical ? cssWidth : cssHeight;
  const scale = Math.min(boxLong / outerLong, boxShort / outerShort);

  const drawnLong = outerLong * scale;
  const drawnShort = outerShort * scale;
  const padLong = (boxLong - drawnLong) / 2 + EDGE_WIDTH * scale;
  const padShort = (boxShort - drawnShort) / 2 + EDGE_WIDTH * scale;

  return {
    scale,
    originX: vertical ? padShort : padLong,
    originY: vertical ? padLong : padShort,
    vertical,
    cssWidth,
    cssHeight,
  };
}

/** Table coordinates → canvas CSS pixels. In a portrait box `x` runs *up* the screen, so the
 * viewer's own end (`x = 0`) is nearest their thumb. */
export function toCanvas(layout: BeerPongLayout, p: BeerPongPoint): { cx: number; cy: number } {
  if (layout.vertical) {
    return {
      cx: layout.originX + p.y * layout.scale,
      cy: layout.originY + (BEERPONG_TABLE_LENGTH - p.x) * layout.scale,
    };
  }
  return { cx: layout.originX + p.x * layout.scale, cy: layout.originY + p.y * layout.scale };
}

/** Canvas CSS pixels → table coordinates. Exact inverse of `toCanvas`. */
export function toTable(layout: BeerPongLayout, cx: number, cy: number): BeerPongPoint {
  if (layout.vertical) {
    return {
      x: BEERPONG_TABLE_LENGTH - (cy - layout.originY) / layout.scale,
      y: (cx - layout.originX) / layout.scale,
    };
  }
  return { x: (cx - layout.originX) / layout.scale, y: (cy - layout.originY) / layout.scale };
}

/**
 * The one coordinate conversion the board has to think about. Everything the *engine*
 * produces — the rack, a throw's frames, an aim — is expressed in whichever player's own
 * frame it belongs to, because that's what lets one simulator and one rulebook serve both
 * ends of the table (see `@awwyeah/shared`'s `mirrorPoint`). The canvas draws from the
 * *viewer's* frame, always, so nothing ever flips mid-game under someone's finger.
 *
 * `flip` is therefore exactly one question: "does this belong to the other end?"
 */
export function toViewFrame<T extends BeerPongPoint>(p: T, flip: boolean): T {
  return flip ? ({ ...p, ...mirrorPoint(p) } as T) : p;
}

/** Sizes the backing store for `devicePixelRatio` and returns a context pre-scaled so every
 * draw call works in CSS pixels. Same helper shape as Pool's and SketchPad's — feature
 * modules may not import each other's internals, so it's copied, not imported. */
export function fitCanvasToDpr(
  canvas: HTMLCanvasElement,
  cssWidth: number,
  cssHeight: number,
): CanvasRenderingContext2D | null {
  const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio ?? 1) : 1;
  const targetWidth = Math.max(1, Math.round(cssWidth * dpr));
  const targetHeight = Math.max(1, Math.round(cssHeight * dpr));
  if (canvas.width !== targetWidth) canvas.width = targetWidth;
  if (canvas.height !== targetHeight) canvas.height = targetHeight;
  canvas.style.width = `${cssWidth}px`;
  canvas.style.height = `${cssHeight}px`;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Tiny fixed-seed generator so the wood grain is the same on every repaint and device. */
function grainRandom(seed: number): () => number {
  let x = seed;
  return () => {
    x = (x * 1103515245 + 12345) % 2147483648;
    return x / 2147483648;
  };
}

function drawSurface(ctx: CanvasRenderingContext2D, layout: BeerPongLayout): void {
  const a = toCanvas(layout, { x: 0, y: 0 });
  const b = toCanvas(layout, { x: BEERPONG_TABLE_LENGTH, y: BEERPONG_TABLE_WIDTH });
  const left = Math.min(a.cx, b.cx);
  const top = Math.min(a.cy, b.cy);
  const width = Math.abs(b.cx - a.cx);
  const height = Math.abs(b.cy - a.cy);
  const edge = EDGE_WIDTH * layout.scale;

  // Rail: darker wood with a soft drop shadow so the table sits on the floor.
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.35)';
  ctx.shadowBlur = edge * 2;
  ctx.shadowOffsetY = edge * 0.6;
  ctx.fillStyle = TABLE_EDGE;
  roundRect(ctx, left - edge, top - edge, width + edge * 2, height + edge * 2, edge * 1.2);
  ctx.fill();
  ctx.restore();

  // Planks: a warm gradient across the short axis, then grain streaks along the long one.
  const across = layout.vertical
    ? ctx.createLinearGradient(left, 0, left + width, 0)
    : ctx.createLinearGradient(0, top, 0, top + height);
  across.addColorStop(0, TABLE_DARK);
  across.addColorStop(0.18, TABLE);
  across.addColorStop(0.55, '#d39b62');
  across.addColorStop(0.85, TABLE);
  across.addColorStop(1, TABLE_DARK);
  ctx.fillStyle = across;
  ctx.fillRect(left, top, width, height);

  ctx.save();
  ctx.beginPath();
  ctx.rect(left, top, width, height);
  ctx.clip();
  const rand = grainRandom(7);
  ctx.lineCap = 'round';
  for (let i = 0; i < 34; i += 1) {
    const y = rand() * BEERPONG_TABLE_WIDTH;
    const wobble = (rand() - 0.5) * 0.05;
    const dark = rand() > 0.35;
    ctx.strokeStyle = dark
      ? `rgba(90,45,15,${(0.06 + rand() * 0.1).toFixed(3)})`
      : `rgba(255,225,180,${(0.05 + rand() * 0.08).toFixed(3)})`;
    ctx.lineWidth = Math.max(0.5, layout.scale * (0.003 + rand() * 0.007));
    const p0 = toCanvas(layout, { x: 0, y });
    const p1 = toCanvas(layout, { x: BEERPONG_TABLE_LENGTH / 2, y: y + wobble });
    const p2 = toCanvas(layout, { x: BEERPONG_TABLE_LENGTH, y: y - wobble * 0.5 });
    ctx.beginPath();
    ctx.moveTo(p0.cx, p0.cy);
    ctx.quadraticCurveTo(p1.cx, p1.cy, p2.cx, p2.cy);
    ctx.stroke();
  }
  // Plank seams: two along the long axis.
  ctx.strokeStyle = 'rgba(70,35,10,0.22)';
  ctx.lineWidth = Math.max(1, layout.scale * 0.006);
  for (const y of [BEERPONG_TABLE_WIDTH / 3, (BEERPONG_TABLE_WIDTH * 2) / 3]) {
    const s0 = toCanvas(layout, { x: 0, y });
    const s1 = toCanvas(layout, { x: BEERPONG_TABLE_LENGTH, y });
    ctx.beginPath();
    ctx.moveTo(s0.cx, s0.cy);
    ctx.lineTo(s1.cx, s1.cy);
    ctx.stroke();
  }
  ctx.restore();

  // Painted lines: a border just inside the rail, and the halfway line.
  const inset = 0.04;
  const i0 = toCanvas(layout, { x: inset, y: inset });
  const i1 = toCanvas(layout, {
    x: BEERPONG_TABLE_LENGTH - inset,
    y: BEERPONG_TABLE_WIDTH - inset,
  });
  ctx.strokeStyle = TABLE_LINE;
  ctx.lineWidth = Math.max(1, layout.scale * 0.012);
  ctx.strokeRect(
    Math.min(i0.cx, i1.cx),
    Math.min(i0.cy, i1.cy),
    Math.abs(i1.cx - i0.cx),
    Math.abs(i1.cy - i0.cy),
  );
  const mid1 = toCanvas(layout, { x: BEERPONG_TABLE_LENGTH / 2, y: 0 });
  const mid2 = toCanvas(layout, { x: BEERPONG_TABLE_LENGTH / 2, y: BEERPONG_TABLE_WIDTH });
  ctx.beginPath();
  ctx.moveTo(mid1.cx, mid1.cy);
  ctx.lineTo(mid2.cx, mid2.cy);
  ctx.stroke();
}

export interface BeerPongCupView {
  at: BeerPongPoint;
  standing: boolean;
}

/** A cup, top down: a soft shadow on the table, the red wall seen past the rim, a glossy
 * rim ring, and beer with a foam edge inside — so it reads as a full cup rather than a
 * target. A cup that's gone leaves a faint wet ring, so the rack's shape stays legible. */
function drawCup(
  ctx: CanvasRenderingContext2D,
  layout: BeerPongLayout,
  cup: BeerPongCupView,
): void {
  const { cx, cy } = toCanvas(layout, cup.at);
  const r = BEERPONG_CUP_RADIUS * layout.scale;
  if (r < 1) return;

  if (!cup.standing) {
    ctx.save();
    ctx.strokeStyle = CUP_GHOST;
    ctx.lineWidth = Math.max(1, r * 0.1);
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.8, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
    return;
  }

  ctx.save();
  ctx.fillStyle = 'rgba(40,15,0,0.28)';
  ctx.beginPath();
  ctx.ellipse(cx + r * 0.16, cy + r * 0.2, r * 1.06, r * 1.02, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  const wall = ctx.createRadialGradient(cx - r * 0.35, cy - r * 0.4, r * 0.1, cx, cy, r);
  wall.addColorStop(0, CUP_RIM);
  wall.addColorStop(0.7, CUP_WALL);
  wall.addColorStop(1, CUP_WALL_DARK);
  ctx.fillStyle = wall;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = 'rgba(255,255,255,0.85)';
  ctx.lineWidth = Math.max(1, r * 0.13);
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.88, 0, Math.PI * 2);
  ctx.stroke();

  const beer = ctx.createRadialGradient(cx - r * 0.2, cy - r * 0.25, r * 0.05, cx, cy, r * 0.76);
  beer.addColorStop(0, BEER);
  beer.addColorStop(1, BEER_DEEP);
  ctx.fillStyle = beer;
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.76, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = FOAM;
  ctx.lineWidth = Math.max(0.75, r * 0.1);
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.7, 0, Math.PI * 2);
  ctx.stroke();

  ctx.strokeStyle = 'rgba(255,255,255,0.55)';
  ctx.lineWidth = Math.max(0.5, r * 0.08);
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.96, Math.PI * 1.05, Math.PI * 1.5);
  ctx.stroke();
}

/** The ball, plus the shadow that is the only reason a top-down view can show an arc at all:
 * the shadow sits on the table at the ball's ground position and tightens as it descends,
 * while the ball itself swells slightly with height. */
function drawBall(
  ctx: CanvasRenderingContext2D,
  layout: BeerPongLayout,
  ball: BeerPongBallPoint,
): void {
  const { cx, cy } = toCanvas(layout, ball);
  const height = Math.max(0, ball.z);
  const lift = 1 + Math.min(1.1, height * 0.55);
  const r = BEERPONG_BALL_RADIUS * layout.scale;

  ctx.save();
  ctx.fillStyle = `rgba(0,0,0,${Math.max(0.06, 0.3 - height * 0.14).toFixed(3)})`;
  ctx.beginPath();
  ctx.ellipse(cx, cy, r * (1 + height * 0.16), r * (0.72 + height * 0.1), 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // A ball below the rim line is inside a cup — draw it dimmed and small so it reads as
  // "gone in" rather than "sitting on top".
  const inCup = ball.z < BEERPONG_CUP_HEIGHT * 0.6 && ball.z > 0;
  ctx.save();
  ctx.globalAlpha = inCup ? 0.55 : 1;
  const shine = ctx.createRadialGradient(
    cx - r * lift * 0.35,
    cy - r * lift * 0.4,
    r * lift * 0.1,
    cx,
    cy,
    r * lift,
  );
  shine.addColorStop(0, '#ffffff');
  shine.addColorStop(0.6, BALL);
  shine.addColorStop(1, '#e4dccb');
  ctx.fillStyle = shine;
  ctx.beginPath();
  ctx.arc(cx, cy, r * lift, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = BALL_EDGE;
  ctx.lineWidth = Math.max(0.5, r * 0.12);
  ctx.stroke();
  ctx.restore();
}

/** A fading streak of where the ball just was — makes the arc's speed readable. */
function drawTrail(
  ctx: CanvasRenderingContext2D,
  layout: BeerPongLayout,
  trail: readonly BeerPongBallPoint[],
): void {
  const r = BEERPONG_BALL_RADIUS * layout.scale;
  ctx.save();
  trail.forEach((p, i) => {
    const { cx, cy } = toCanvas(layout, p);
    const k = (i + 1) / (trail.length + 1);
    const lift = 1 + Math.min(1.1, Math.max(0, p.z) * 0.55);
    ctx.fillStyle = `rgba(255,255,255,${(k * 0.35).toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(cx, cy, r * lift * (0.4 + k * 0.5), 0, Math.PI * 2);
    ctx.fill();
  });
  ctx.restore();
}

/** Beer leaping out of the cup the ball just dropped into: a burst of droplets thrown
 * outward and falling back, plus a ring spreading on the surface. `t` runs 0 → 1. */
export interface BeerPongSplash {
  at: BeerPongPoint;
  t: number;
}

const SPLASH_DROPS = Array.from({ length: 12 }, (_, i) => {
  const angle = (i / 12) * Math.PI * 2 + (i % 2) * 0.2;
  return { dx: Math.cos(angle), dy: Math.sin(angle), reach: 1.4 + ((i * 7) % 5) * 0.22 };
});

function drawSplash(
  ctx: CanvasRenderingContext2D,
  layout: BeerPongLayout,
  splash: BeerPongSplash,
): void {
  const { cx, cy } = toCanvas(layout, splash.at);
  const r = BEERPONG_CUP_RADIUS * layout.scale;
  const t = Math.min(1, Math.max(0, splash.t));
  const fade = 1 - t;
  ctx.save();
  ctx.strokeStyle = `rgba(255,248,228,${(0.8 * fade).toFixed(3)})`;
  ctx.lineWidth = Math.max(1, r * 0.14 * fade);
  ctx.beginPath();
  ctx.arc(cx, cy, r * (0.5 + t * 1.3), 0, Math.PI * 2);
  ctx.stroke();
  for (const drop of SPLASH_DROPS) {
    const out = r * drop.reach * Math.sin(t * Math.PI * 0.85);
    const x = cx + drop.dx * out;
    const y = cy + drop.dy * out - r * 0.9 * Math.sin(t * Math.PI);
    ctx.fillStyle = `rgba(240,190,90,${(0.9 * fade).toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(x, y, Math.max(0.8, r * 0.16 * (1 - t * 0.5)), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

export interface BeerPongAimOverlay {
  /** Where the ball leaves the hand, in view coordinates. */
  from: BeerPongPoint;
  /** Where an unobstructed throw comes back down, in view coordinates. */
  to: BeerPongPoint;
  power: number;
  /** Drag point behind the throwing spot — draws the pull-back line. */
  pullTo?: BeerPongPoint;
  cancel: boolean;
}

function drawAim(
  ctx: CanvasRenderingContext2D,
  layout: BeerPongLayout,
  aim: BeerPongAimOverlay,
  turnColor: string,
): void {
  const from = toCanvas(layout, aim.from);
  const to = toCanvas(layout, aim.to);

  ctx.save();
  ctx.setLineDash([5, 7]);
  ctx.lineWidth = 2;
  ctx.strokeStyle = aim.cancel ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.8)';
  ctx.beginPath();
  ctx.moveTo(from.cx, from.cy);
  ctx.lineTo(to.cx, to.cy);
  ctx.stroke();
  ctx.setLineDash([]);

  // The landing reticle: exactly the point the capture test uses, so what it promises is
  // "the ball arrives here", never "this goes in".
  if (!aim.cancel) {
    ctx.strokeStyle = turnColor;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(to.cx, to.cy, BEERPONG_BALL_RADIUS * layout.scale * 1.6, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(to.cx, to.cy, Math.max(1, BEERPONG_BALL_RADIUS * layout.scale * 0.25), 0, Math.PI * 2);
    ctx.fillStyle = turnColor;
    ctx.fill();
  }

  if (aim.pullTo) {
    const pull = toCanvas(layout, aim.pullTo);
    ctx.strokeStyle = aim.cancel ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.55)';
    ctx.lineWidth = Math.max(2, layout.scale * 0.018);
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(from.cx, from.cy);
    ctx.lineTo(pull.cx, pull.cy);
    ctx.stroke();
  }

  // Power ring around the throwing spot, in the thrower's member colour.
  if (!aim.cancel && aim.power > 0) {
    ctx.strokeStyle = turnColor;
    ctx.lineWidth = Math.max(2, BEERPONG_BALL_RADIUS * layout.scale * 0.5);
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.arc(
      from.cx,
      from.cy,
      BEERPONG_BALL_RADIUS * layout.scale * 2,
      -Math.PI / 2,
      -Math.PI / 2 + Math.PI * 2 * aim.power,
    );
    ctx.stroke();
  }
  ctx.restore();
}

export interface BeerPongSceneOptions {
  /** Every cup of both racks, in view coordinates, standing or ghosted. */
  cups: readonly BeerPongCupView[];
  /** Member colour of whoever is throwing — the one theme-derived value on the table. */
  turnColor: string;
  /** The ball mid-flight, if there's an animation running. */
  ball?: BeerPongBallPoint;
  /** Where the ball waits between throws, in view coordinates. */
  restingBall?: BeerPongPoint;
  aim?: BeerPongAimOverlay;
  /** The ball's last few positions, oldest first. */
  trail?: readonly BeerPongBallPoint[];
  splash?: BeerPongSplash;
}

/** One full repaint. Cheap enough (a rectangle, twenty cups and one ball) that incremental
 * drawing would be pure complexity — same call every frame of an animation. */
export function drawTable(
  ctx: CanvasRenderingContext2D,
  layout: BeerPongLayout,
  scene: BeerPongSceneOptions,
): void {
  ctx.clearRect(0, 0, layout.cssWidth, layout.cssHeight);
  drawSurface(ctx, layout);
  for (const cup of scene.cups) drawCup(ctx, layout, cup);
  if (scene.splash) drawSplash(ctx, layout, scene.splash);
  if (scene.trail && scene.ball) drawTrail(ctx, layout, scene.trail);
  if (scene.ball) drawBall(ctx, layout, scene.ball);
  else if (scene.restingBall) drawBall(ctx, layout, { ...scene.restingBall, z: 0 });
  if (scene.aim) drawAim(ctx, layout, scene.aim, scene.turnColor);
}
